import { Head } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { authApiOrigin } from "@/lib/_url/_origin";
import { fetchAuthApiSession } from "@/lib/_url/_session";
import { hairbookSalonUrl } from "@/lib/_url/_external";
import { NoroshiPhoneAuthModal } from "./_noroshi-phone-auth-modal";
import { salonpageReserveHref } from "./_noroshi-reserve";
import type { SalonpageRedirectProps as Props } from "./_props";

type SessionState = "checking" | "signed-in" | "signed-out";

function redirectUrl(salon: Props["salon"], to: Props["to"]): string | null {
  if (!salon) return null;
  if (to === "hairbook") return salon.sourceUrl || hairbookSalonUrl(Number(salon.id));
  if (to === "instagram") return salon.instagramUrl;
  if (to === "reserve") return hotpepperReserveUrl(salon.reserveUrl);
  return null;
}

function hotpepperReserveUrl(value: string | null): string | null {
  if (!value) return null;

  try {
    const url = new URL(value);
    if (url.hostname === "beauty.hotpepper.jp" && url.pathname === "/CSP/bt/reserve/") {
      url.searchParams.set("ch", "1");
    }
    return url.toString();
  } catch {
    return value;
  }
}

function internalReserveUrl(
  salon: Props["salon"],
  menuId: Props["menuId"],
  staffId: Props["staffId"],
  mode: Props["mode"],
): string | null {
  if (!salon) return null;
  return salonpageReserveHref({
    salonId: String(salon.id),
    menuId,
    staffId,
    mode,
  });
}

function LoginWallPanel({
  destinationUrl,
  salonName,
  onStartAuth,
}: {
  destinationUrl: string;
  salonName: string;
  onStartAuth: () => void;
}) {
  return (
    <>
      <div
        className="fixed inset-0 z-[9999] bg-transparent"
        aria-hidden="true"
      />
      <div className="fixed inset-x-0 bottom-0 z-[10000] flex items-end justify-center">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-wall-title"
          className="w-full max-w-md animate-[login-wall-slide-up_260ms_cubic-bezier(0.22,1,0.36,1)] rounded-t-2xl bg-white px-6 pb-8 pt-6 shadow-2xl"
        >
          <div className="mx-auto mb-6 h-1 w-10 rounded-full bg-gray-300" />

          <div className="mb-6 flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 text-gray-700">
              <svg
                aria-hidden="true"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V7.75a4.5 4.5 0 0 0-9 0v2.75m-.75 0h10.5A1.75 1.75 0 0 1 19 12.25v6A1.75 1.75 0 0 1 17.25 20H6.75A1.75 1.75 0 0 1 5 18.25v-6a1.75 1.75 0 0 1 1.75-1.75Z"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2
                id="login-wall-title"
                className="text-[22px] font-bold leading-snug text-gray-900"
              >
                予約を続ける前に
              </h2>
              <p className="text-sm leading-relaxed text-gray-500">
                電話番号認証すると、次回から予約がスムーズになります
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onStartAuth}
            aria-label={`${salonName}の予約を続けるため電話番号認証する`}
            className="flex w-full items-center justify-center rounded-full bg-red-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            電話番号認証する
          </button>
          <a
            href={destinationUrl}
            aria-label={`${salonName}の予約ページへログインせず進む`}
            className="mt-3 flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
          >
            ログインせず予約ページへ進む
          </a>
        </section>
      </div>
    </>
  );
}

export default function SalonpageRedirect({ salon, menu, featureToggles, menuId, staffId, mode, to, appStage }: Props) {
  const [sessionState, setSessionState] = useState<SessionState>("checking");
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const useNoroshiReserve =
    to === "reserve" && !!featureToggles?.noroshiReserve;
  const url = useNoroshiReserve
    ? internalReserveUrl(salon, menuId, staffId, mode)
    : redirectUrl(salon, to);
  const destinationName = to === "instagram" ? "Instagram" : to === "reserve" ? "予約ページ" : "Hairbook";
  const destinationText = menu ? `「${menu.name}」の${destinationName}` : destinationName;
  const requiresLogin =
    to === "reserve" &&
    !useNoroshiReserve &&
    appStage === "main" &&
    !!featureToggles?.useLoginWall;
  useEffect(() => {
    if (!requiresLogin) {
      setSessionState("signed-in");
      return;
    }

    let active = true;

    fetchAuthApiSession(authApiOrigin())
      .then((session) => {
        if (active) {
          setSessionState(session.signedIn ? "signed-in" : "signed-out");
        }
      })
      .catch(() => {
        if (active) setSessionState("signed-out");
      });

    return () => {
      active = false;
    };
  }, [requiresLogin]);

  useEffect(() => {
    if (!requiresLogin || sessionState === "signed-in") return;

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [requiresLogin, sessionState]);

  useEffect(() => {
    if (!url || sessionState !== "signed-in") return;

    if (useNoroshiReserve) {
      window.location.replace(url);
      return;
    }

    const timer = setTimeout(() => {
      window.location.replace(url);
    }, 900);

    return () => {
      clearTimeout(timer);
    };
  }, [sessionState, url, useNoroshiReserve]);

  if (!salon || !url) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf7] text-[#0a0a0a] font-serif">
        <div className="text-center">
          <h1 className="text-xl mb-4">リンクが存在しません</h1>
          <a href="/salons" className="text-sm text-black/50 underline">トップへ戻る</a>
        </div>
      </div>
    );
  }

  if (useNoroshiReserve) {
    return (
      <>
        <Head title={`${salon.name} - 予約ページへ移動`} />
        <div className="flex min-h-screen items-center justify-center bg-[#f4f2ef]">
          <div className="text-center">
            <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-red-600" />
            <p className="mt-4 text-sm text-gray-500">予約ページへ移動しています</p>
          </div>
        </div>
      </>
    );
  }

  const showLoginWall = requiresLogin && sessionState === "signed-out";
  const isCheckingSession = requiresLogin && sessionState === "checking";
  const isMoving = !showLoginWall;
  const title = showLoginWall ? "予約内容" : `${destinationName}へ移動中です`;
  const description = showLoginWall
    ? "予約ページへ進む前に本人確認をお願いします。"
    : "まもなく外部サイトに切り替わります。";
  const linkLabel = isCheckingSession
    ? "本人確認の状態を確認中です"
    : "自動で切り替わらない場合はこちら";

  return (
    <>
      <Head title={`${salon.name} - 外部サイトへ移動`} />

      <div className="min-h-screen bg-[#f4f2ef] px-4 pb-80 pt-5 text-[#0a0a0a] antialiased">
        <main className="mx-auto w-full max-w-md">
          <header className="mb-4">
            <p className="text-[11px] font-semibold text-red-600">Hairbook</p>
            <h1 className="mt-1 text-xl font-bold leading-none text-gray-950">
              {title}
            </h1>
          </header>

          <article className="overflow-hidden rounded-lg bg-white shadow-[0_18px_50px_rgba(22,24,28,0.12)]">
            <div className="space-y-4 p-5">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-red-600">サロン</p>
                <h2 className="text-[24px] font-bold leading-snug text-gray-950">
                  {salon.name}
                </h2>
                {menu && (
                  <p className="text-sm font-semibold text-gray-700">
                    {menu.name}
                  </p>
                )}
                <p className="text-sm font-medium leading-relaxed text-gray-500">
                  {description}
                </p>
              </div>

              {isMoving && (
                <div className="space-y-4 rounded-lg bg-gray-50 px-4 py-5 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200">
                    <span className="redirect-spinner h-6 w-6 rounded-full border-2 border-gray-200 border-t-red-600" />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {destinationText}へ移動しています
                    </p>
                    <div className="redirect-progress h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <span className="block h-full w-1/3 rounded-full bg-red-600" />
                    </div>
                    <div className="flex items-center justify-center gap-1 pt-1">
                      <span className="redirect-dot" />
                      <span className="redirect-dot redirect-dot-delay-1" />
                      <span className="redirect-dot redirect-dot-delay-2" />
                    </div>
                  </div>
                </div>
              )}

              {isMoving && (
                <a
                  href={url}
                  aria-disabled={requiresLogin && sessionState !== "signed-in"}
                  className="inline-flex h-12 w-full items-center justify-center rounded-full bg-red-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 aria-disabled:pointer-events-none aria-disabled:bg-red-600/40"
                >
                  {linkLabel}
                </a>
              )}
            </div>
          </article>
        </main>
      </div>

      <style>{`
        .redirect-spinner {
          animation: redirect-spin 860ms linear infinite;
        }

        .redirect-progress > span {
          animation: redirect-progress 1100ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }

        .redirect-dot {
          display: block;
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          background: #dc2626;
          animation: redirect-dot 900ms ease-in-out infinite;
        }

        .redirect-dot-delay-1 {
          animation-delay: 140ms;
        }

        .redirect-dot-delay-2 {
          animation-delay: 280ms;
        }

        @keyframes redirect-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes redirect-progress {
          from {
            transform: translateX(-110%);
          }
          to {
            transform: translateX(310%);
          }
        }

        @keyframes redirect-dot {
          0%,
          100% {
            opacity: 0.35;
            transform: translateY(0);
          }
          50% {
            opacity: 1;
            transform: translateY(-3px);
          }
        }

        @keyframes login-wall-slide-up {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {showLoginWall && (
        <LoginWallPanel
          destinationUrl={url}
          salonName={salon.name}
          onStartAuth={() => setAuthModalOpen(true)}
        />
      )}
      {authModalOpen && (
        <NoroshiPhoneAuthModal
          origin={authApiOrigin()}
          category="sms_login_wall_redirect"
          sourceId={String(salon.id)}
          completeOnCode
          onAuthenticated={() => {
            setSessionState("signed-in");
            setAuthModalOpen(false);
          }}
          onClose={() => setAuthModalOpen(false)}
        />
      )}
    </>
  );
}
